// frontend/src/pages/EuGrant.js
// 🇪🇺 Pályázati adatlap — dátumozott pillanatfelvétel EU-s pályázatokhoz.
//
// A dashboard többi oldala ÉLŐ adatot mutat. Ez szándékosan más: azt a
// kérdést szolgálja ki, hogy „mit írhatok be MA a pályázatba úgy, hogy két év
// múlva is igazolni tudom". Ezért van rajta dátum, módszertan és fenntartások.
import React, { useEffect, useState } from 'react';

const C = {
  panel: '#1a1d27', panel2: '#22252f', border: '#2a2e3a',
  text: '#e4e6eb', muted: '#8b8fa3', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444', purple: '#a855f7',
};
const card = { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 };
const h3 = { fontSize: 13, color: C.muted, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const th = { padding: '6px 8px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` };
const td = { padding: '6px 8px', borderBottom: '1px solid #20242e' };

const fmt = (v, unit) => (v == null ? '—' : `${typeof v === 'number' ? v.toLocaleString('hu-HU') : v}${unit ? ' ' + unit : ''}`);

function Kpi({ label, value, unit, sub, color = C.accent }) {
  return (
    <div style={{ ...card, marginBottom: 0 }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{fmt(value, unit)}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function EuGrant() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);

  const load = (refresh) => {
    setLoading(true); setErr(null);
    fetch('/api/eu-grant/summary' + (refresh ? '?refresh=1' : ''), { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).error || 'HTTP ' + r.status); return r.json(); })
      .then(setD).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(false); }, []);

  // format: 'xlsx' | 'pdf' — ugyanaz a pillanatfelvétel, két formában. A PDF
  //   mellékletnek/nyomtatásra, az xlsx a számok ellenőrzésére.
  const download = (format) => {
    setExporting(format); setExportMsg(null);
    fetch(format === 'pdf' ? '/api/eu-grant/export.pdf' : '/api/eu-grant/export', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          let msg = 'HTTP ' + r.status;
          try { const j = await r.json(); if (j.error) msg = j.error; } catch (e) { /* nem JSON */ }
          throw new Error(msg);
        }
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-tourmate-palyazati-adatlap-${(d && d.asOfDate) || 'export'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setExportMsg({ ok: true, text: 'Letöltés elindult. Ezt a fájlt tedd el — ez a dátumozott bizonyítékod.' });
      })
      .catch((e) => setExportMsg({ ok: false, text: e.message }))
      .finally(() => setExporting(false));
  };

  if (loading && !d) return <div style={{ padding: 24, color: C.muted }}>Számolás… (teljes adatbázis-pásztázás, eltarthat egy percig)</div>;
  if (err) return <div style={{ padding: 24, color: C.bad }}>Hiba: {err}</div>;
  if (!d) return null;

  const R = d.reach, G = d.geography, H = d.heritage, E = d.environment, GA = d.galileo, U = d.unitEconomics;

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: C.text }}>🇪🇺 Pályázati adatlap</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => load(true)} disabled={loading}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.panel2, color: C.text, cursor: loading ? 'default' : 'pointer', fontSize: 13 }}>
            {loading ? '⏳ Számolás…' : '↻ Újraszámolás'}
          </button>
          <button onClick={() => download('xlsx')} disabled={!!exporting}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: exporting ? C.border : C.accent, color: exporting ? C.muted : '#0a0d14', fontWeight: 700, cursor: exporting ? 'default' : 'pointer', fontSize: 13 }}>
            {exporting === 'xlsx' ? '⏳ Készítés…' : '⬇ Excel letöltése'}
          </button>
          <button onClick={() => download('pdf')} disabled={!!exporting}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: exporting ? C.border : C.accent, color: exporting ? C.muted : '#0a0d14', fontWeight: 700, cursor: exporting ? 'default' : 'pointer', fontSize: 13 }}>
            {exporting === 'pdf' ? '⏳ Készítés…' : '⬇ PDF letöltése'}
          </button>
        </div>
      </div>

      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
        Az állapot dátuma: <b style={{ color: C.text }}>{d.asOfDate}</b>. A dashboard többi oldala élő adatot mutat —
        ez szándékosan <b style={{ color: C.text }}>lefagyasztott pillanatfelvétel</b>, mert egy EU-s audit évekkel
        később is kérheti, hogy igazold a benyújtott számot. Töltsd le az Excelt, és tedd el: tartalmazza a
        számítási módszert és a fenntartásokat is, tehát önmagában is megáll. A PDF ugyanezt tartalmazza
        mellékletnek vagy nyomtatásra.
      </div>

      {exportMsg && (
        <div style={{ ...card, borderColor: exportMsg.ok ? C.ok : C.bad, color: exportMsg.ok ? C.ok : C.bad, fontSize: 13 }}>
          {exportMsg.ok ? '✅ ' : '❌ '}{exportMsg.text}
        </div>
      )}

      {/* Fenntartások — feltűnő helyen, mert ezeket nem szabad átnézni felette */}
      <div style={{ ...card, borderColor: C.warn }}>
        <div style={{ ...h3, color: C.warn, marginBottom: 8 }}>⚠️ Amit a pályázatban nem szabad elhallgatni</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 12, lineHeight: 1.7 }}>
          {d.caveats.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </div>

      <div style={{ ...h3 }}>Elérés és növekedés</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi label="Regisztrált felhasználó" value={R.registeredUsers} unit="fő" />
        <Kpi label="Terméket használt" value={R.usersWithNarration} unit="fő" sub={`aktiválás: ${fmt(R.activationPct, '%')}`} color={C.ok} />
        <Kpi label="Fizető felhasználó" value={R.payingUsers} unit="fő" sub={`${fmt(R.payingSharePct, '%')} a regisztráltakból`} color={C.purple} />
        <Kpi label="Narrációk összesen" value={R.totalNarrations} unit="db" sub={`${fmt(R.narrationsPerActiveUser)} / aktív user`} color={C.warn} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {/* Földrajz */}
        <div style={card}>
          <div style={h3}>Földrajzi megoszlás</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Nagyváros', G.metro, G.metroPct, C.accent],
                ['Város / agglomeráció', G.city, G.cityPct, C.ok],
                ['Vidéki / kisváros', G.rural, G.ruralPct, C.warn],
                ['Referencialistán kívül', G.outOfScope, G.outOfScopePct, C.muted],
              ].map(([l, c, p, col]) => (
                <tr key={l}>
                  <td style={{ ...td, color: col }}>{l}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(c)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(p, '%')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>
            Közelítés referencia-városközpontoktól mért távolság alapján — <b>nem</b> az Eurostat DEGURBA
            osztályozás. {fmt(G.noCoordinates)} narráció koordináta nélkül, ezek kimaradnak.
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
            Egyedi települések: <b style={{ color: G.cityFieldPopulated ? C.text : C.warn }}>
              {G.cityFieldPopulated ? fmt(G.distinctCities) : 'nem gyűjtött'}
            </b>
            {!G.cityFieldPopulated && (
              <span style={{ fontSize: 11, color: C.muted }}> — az app nem írja a <code>city</code> mezőt a tranzakciókba</span>
            )}<br />
            Több térségben használ: <b style={{ color: C.text }}>{fmt(G.multiRegionPct, '%')}</b> ({fmt(G.multiRegionUsers)} user)<br />
            Kiterjedés mediánja: <b style={{ color: C.text }}>{fmt(G.medianUserSpanKm, 'km')}</b> · max {fmt(G.maxUserSpanKm, 'km')}
          </div>
        </div>

        {/* Örökség */}
        <div style={card}>
          <div style={h3}>Kulturális örökség lefedettség</div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'baseline', marginBottom: 6, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: C.purple }}>{fmt(H.heritageSharePct, '%')}</div>
              <div style={{ fontSize: 10, color: C.muted }}>összes narrációra</div>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: C.ok }}>{fmt(H.heritageSharePctOfTyped, '%')}</div>
              <div style={{ fontSize: 10, color: C.muted }}>tipizált narrációkra</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
            örökségi, vallási, múzeumi és építészeti helyszínek együtt.
            {H.untypedSharePct > 10 && (
              <> <b style={{ color: C.warn }}>{fmt(H.untypedSharePct, '%')}</b> narrációnál egyáltalán nincs POI-típus,
              ezért a bal oldali szám alulmutat — pályázatba a jobb oldali a becsületesebb, az alacsonyabb
              mellé pedig oda kell írni a hiányzó típusok arányát.</>
            )}
          </div>
          {H.byMethod && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, paddingBottom: 12,
              borderBottom: `1px solid ${C.border}`, lineHeight: 1.8 }}>
              <b style={{ color: C.text }}>Mi alapján sorolódott be?</b><br />
              POI-típus alapján (adat): <b style={{ color: C.ok }}>{fmt(H.byMethod.type)}</b> ·
              forrás alapján: <b style={{ color: C.text }}>{fmt(H.byMethod.source)}</b> ·
              <span style={{ color: C.warn }}> név alapján (heurisztika): <b>{fmt(H.byMethod.name)}</b></span> ·
              besorolhatatlan: <b style={{ color: C.text }}>{fmt(H.byMethod.none)}</b>
              <div style={{ marginTop: 6 }}>
                Örökség-arány <b style={{ color: C.ok }}>csak típusból</b>: {fmt(H.heritageFromTypePct, '%')} ·
                <span style={{ color: C.warn }}> névvel kiegészítve: {fmt(H.heritageWithNameFallbackPct, '%')}</span>
              </div>
              <div style={{ marginTop: 6, color: '#6b7280' }}>
                A név-alapú besorolás <b>heurisztika, nem adat</b>. Pályázatba az alacsonyabb,
                típus-alapú szám a védhető; a magasabbat csak akkor közöld, ha odaírod, hogy
                POI-nevekből következtetett.
              </div>
            </div>
          )}
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {H.groups.filter((g) => g.count > 0).map((g) => (
                <tr key={g.id}>
                  <td style={td}>{g.label}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(g.count)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(g.sharePct, '%')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {H.untypedDiagnostics && H.untypedNarrations > 0 && (
            <details style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: C.warn }}>
                Mi az a {fmt(H.untypedNarrations)} típus nélküli narráció?
              </summary>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.8 }}>
                Van OpenTripMap azonosítója: <b style={{ color: C.text }}>{fmt(H.untypedDiagnostics.withOpenTripMapId)}</b> ·
                van koordinátája: <b style={{ color: C.text }}>{fmt(H.untypedDiagnostics.withCoordinates)}</b>
                <div style={{ marginTop: 8, fontWeight: 700, color: C.text }}>Forrás szerint</div>
                {H.untypedDiagnostics.bySource.map((r) => (
                  <div key={r.key}>{r.key}: <b style={{ color: C.text }}>{fmt(r.count)}</b> ({fmt(r.sharePct, '%')})</div>
                ))}
                <div style={{ marginTop: 8, fontWeight: 700, color: C.text }}>Hónap szerint</div>
                <div>{H.untypedDiagnostics.byMonth.map((r) => `${r.key}: ${r.count}`).join(' · ')}</div>
                <div style={{ marginTop: 8, fontWeight: 700, color: C.text }}>Leggyakoribb POI-nevek</div>
                {H.untypedDiagnostics.topNames.slice(0, 12).map((r) => (
                  <div key={r.key}>{r.key} <span style={{ color: '#6b7280' }}>× {r.count}</span></div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Nyelvek */}
        <div style={card}>
          <div style={h3}>Nyelvi lefedettség — {fmt(d.languages.distinct)} nyelv</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {d.languages.breakdown.slice(0, 15).map((l) => (
                <tr key={l.key}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{l.key}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(l.count)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(l.sharePct, '%')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>
            Tényleges használat szerint, nem a felület kínálata.
          </div>
        </div>

        {/* Galileo + környezet + egységköltség */}
        <div style={card}>
          <div style={h3}>Technológia és fenntarthatóság</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 2 }}>
            Galileo-hasznosulás: <b style={{ color: C.accent }}>{fmt(GA.utilisationPooled, '%')}</b>
            <span style={{ fontSize: 11 }}> (galileoUsed / galileoVisible)</span><br />
            Dual-frequency mérések: <b style={{ color: C.text }}>{fmt(GA.dualFreqShare, '%')}</b><br />
            GNSS telemetria rekord: <b style={{ color: C.text }}>{fmt(GA.records)}</b><br />
            <span style={{ display: 'block', height: 8 }} />
            Változó költség / narráció: <b style={{ color: U.reliable ? C.ok : C.warn }}>
              {U.costPerNarrationUsd == null ? '—' : '$' + U.costPerNarrationUsd}
            </b>{!U.reliable && <span style={{ fontSize: 11, color: C.warn }}> ⚠️</span>}
            <span style={{ fontSize: 11 }}> (csak AI-költség)</span><br />
            Teljes önköltség / narráció: <b style={{ color: C.muted }}>
              {U.fullyLoadedCostPerNarrationUsd == null ? '—' : '$' + U.fullyLoadedCostPerNarrationUsd}
            </b><span style={{ fontSize: 11 }}> (a fix szerverrel együtt)</span><br />
            Becsült CO₂ / narráció: <b style={{ color: C.text }}>{fmt(E.gPerNarration, 'g')}</b><br />
            Becsült CO₂ összesen: <b style={{ color: C.text }}>{fmt(E.totalKgCo2, 'kg')}</b>
          </div>
          {!U.reliable && (
            <div style={{ fontSize: 11, color: C.warn, marginTop: 10, lineHeight: 1.6 }}>
              ⚠️ A költség/narráció <b>nem megbízható</b>: a napi költségtábla a tényleges
              {' '}{fmt(U.measuredNarrations)} narrációnak csak <b>{fmt(U.localTableCoveragePct, '%')}</b>-át
              ismeri ({fmt(U.narrationsInLocalTable)} db), tehát a költségoldal is hiányos. Ez a szám
              addig nem közölhető, amíg a napi gyűjtő lánc nincs visszatöltve.
            </div>
          )}
          <div style={{ fontSize: 11, color: C.warn, marginTop: 10, lineHeight: 1.6 }}>
            A CO₂ <b>becslés</b>: {E.narrations.toLocaleString('hu-HU')} narráció × {E.whPerNarration} Wh
            × {E.gCo2PerKwh} g/kWh. Az együtthatók env változóval felülírhatók, és szerepelnek az Excel
            módszertani lapján is.
          </div>
        </div>
      </div>

      {/* Megtartás — KUMULATÍV. A pontbeli havi kohorsz egy utazási appnál
          félrevezető, ezért „visszatért-e egyáltalán N hónapon belül" a kérdés. */}
      <div style={card}>
        <div style={h3}>Megtartás — kumulatív ablakok</div>
        {d.retention.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Még nincs elég adat kohorsz-számításhoz.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr>
                <th style={th}>Kohorsz (első használat)</th>
                <th style={{ ...th, textAlign: 'right' }}>Méret</th>
                <th style={{ ...th, textAlign: 'right' }}>Eltelt</th>
                <th style={{ ...th, textAlign: 'right' }}>Bármikor később</th>
                <th style={{ ...th, textAlign: 'right' }}>3 hónapon belül</th>
                <th style={{ ...th, textAlign: 'right' }}>6 hónapon belül</th>
                <th style={{ ...th, textAlign: 'right' }}>12 hónapon belül</th>
              </tr></thead>
              <tbody>
                {d.retention.map((r) => (
                  <tr key={r.cohort}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.cohort}</td>
                    <td style={{ ...td, textAlign: 'right', color: r.size < 20 ? C.warn : C.text }}>
                      {r.size}{r.size < 20 ? ' ⚠️' : ''}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: C.muted }}>{r.monthsElapsed} hó</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: r.anyLaterPct >= 20 ? C.ok : C.text }}>
                      {fmt(r.anyLaterPct, '%')}
                    </td>
                    {['w3', 'w6', 'w12'].map((w) => (
                      <td key={w} style={{ ...td, textAlign: 'right', color: r[w + 'Observable'] ? C.text : C.muted }}>
                        {r[w + 'Observable']
                          ? fmt(r[w + 'Pct'], '%')
                          : <span title="Az ablak még nem telt el — ez nem nulla megtartás, hanem hiányzó idő">nincs még adat</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.7 }}>
          A kohorsz az <b>első narráció</b> hónapja. A „N hónapon belül" azt jelenti, hogy a felhasználó az
          első naptári hónapja UTÁN legalább egyszer visszatért N hónapon belül — <b>kumulatív</b>, nem pontbeli.
          A ⚠️ a 20 fő alatti kohorszokat jelöli: ott egyetlen ember is nagy százalékot mozdít, tehát a szám zaj.
          Részletesebb utazás-alapú elemzés: <b>Felhasználók → 🧳 Utazások</b>.
        </div>
      </div>

      <div style={{ height: 40 }} />
    </div>
  );
}
